import { useEffect, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import Snackbar from "./components/Snackbar";
import SimpleModal from "./components/Modal";
import { ChangePass as ChangePassUntyped } from "./components/ChangePassword";
import useToken from "./hooks/useToken";
import { commonStrings } from "./components/StringBundle";
import { IDLE_ACTIVITY_EVENTS, PASSWORD_EXPIRY_DAYS, LAST_ACTIVITY_KEY, ACTIVITY_THROTTLE_MS, LOGOUT_REASON_KEY } from "./constants/autoLogout";
import type { SnackState, ChangePassProps, StoredPreference } from "./types";

// ChangePassword.js is untyped, so TS infers this component as accepting no
// props at all — bridge to the shape it actually reads.
const ChangePass = ChangePassUntyped as ComponentType<ChangePassProps>;

const AppLogout = ({ children }: { children: ReactNode }) => {
  const snackInitial: SnackState = { open: false, message: "", severity: "" };
  const [snack, setSnack] = useState<SnackState>(snackInitial);
  const [openChangePass, setChangePass] = useState<boolean>(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let isActivityThrottled = false;
  let activityThrottleTimeout: ReturnType<typeof setTimeout> | undefined;

  const { getPreference, removeToken } = useToken();
  const location = useLocation();

  // Outer safety-net timer — deliberately longer than AppBar's own
  // warning-then-logout window (30 min), so this silent fallback never races
  // and preempts that user-facing warning. Only matters if AppBar isn't
  // mounted at all (e.g. some route without it) — otherwise AppBar's own
  // timer fires first.
  const IDLE_LOGOUT_MS = 2100000; // 35 min
  const handleLogoutTimer = (): void => {
    timer = setTimeout(() => {
      // This timer can fire late if the browser froze/throttled this backgrounded tab —
      // in that case it may have missed the 'storage' event another tab's activity would
      // have used to reset it. Re-check the shared timestamp before actually ending the
      // session: a stale local timer must not log out a session that's active elsewhere.
      const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || "0", 10);
      if (Date.now() - lastActivity < IDLE_LOGOUT_MS) {
        handleLogoutTimer();
        return;
      }
      // Nothing to protect yet (e.g. still sitting on the login/OTP screen, or
      // already logged out) — keep listening instead of hitting the real backend
      // /logout and reloading the page for no reason. "currentUserId" is the same
      // "is there a session" marker useToken/AppBar's idle-logout check reads.
      if (!localStorage.getItem("currentUserId")) {
        handleLogoutTimer();
        return;
      }
      resetTimer();
      Object.values(IDLE_ACTIVITY_EVENTS).forEach((item) => {
        window.removeEventListener(item, handleActivitySignal);
      });
      logoutAction();
    }, IDLE_LOGOUT_MS);
  };
  const resetTimer = (): void => {
    if (timer) clearTimeout(timer);
  };

  // when component mounts, it adds an event listeners to the window
  // each time any of the event is triggered, i.e on mouse move, click, scroll, keypress etc, the timer to logout user after 18 minutes of inactivity resets (see handleLogoutTimer for why this is longer than AppBar's own 15-minute idle window).
  // However, if none of the event is triggered within that window, that is app is inactive, the app automatically logs out.
  // Throttled to ACTIVITY_THROTTLE_MS — mousemove/keydown/etc. fire far more often than that, so most calls are ignored.
  const handleActivitySignal = (): void => {
    if (isActivityThrottled) return;
    isActivityThrottled = true;
    activityThrottleTimeout = setTimeout(() => {
      isActivityThrottled = false;
    }, ACTIVITY_THROTTLE_MS);
    resetTimer();
    handleLogoutTimer();
  };

  useEffect(() => {
    Object.values(IDLE_ACTIVITY_EVENTS).forEach((item) => {
      window.addEventListener(item, handleActivitySignal);
    });

    // Another tab writing LAST_ACTIVITY_KEY counts as activity here too, so this tab's own fallback doesn't fire under it.
    const handleStorageActivity = (e: StorageEvent): void => {
      if (e.key !== LAST_ACTIVITY_KEY || !e.newValue) return;
      handleActivitySignal();
    };
    window.addEventListener("storage", handleStorageActivity);

    // Another tab logging out writes LOGOUT_REASON_KEY — read it off the event itself, not a later localStorage.getItem, since that tab's own clear() may already have wiped it.
    const handleLoggedOutElsewhere = (e: StorageEvent): void => {
      if (e.key !== LOGOUT_REASON_KEY || !e.newValue) return;
      if (e.newValue === "auto") {
        sessionStorage.setItem("autoLogoutMessage", "1");
      }
      window.location.pathname = "/login";
    };
    window.addEventListener("storage", handleLoggedOutElsewhere);

    return () => {
      window.removeEventListener("storage", handleStorageActivity);
      window.removeEventListener("storage", handleLoggedOutElsewhere);
      clearTimeout(activityThrottleTimeout);
    };
  }, []);

  // Re-checks on every route change, since login writes the real preference and then navigates client-side without remounting AppLogout.
  useEffect(() => {
    if (!openChangePass && !snack.open) {
      checkPasswordDays();
    }
  }, [location.pathname]);

  const checkPasswordDays = (): void => {
    const preference = getPreference() as StoredPreference | null;
    const date = new Date(preference?.lastPasswordDate ?? "");
    const currentDate = new Date();

    const differenceInMilliseconds = currentDate.getTime() - date.getTime();
    const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
    const isExpired = differenceInDays > PASSWORD_EXPIRY_DAYS;

    if (isExpired) {
      setSnack({
        open: true,
        message: commonStrings.passwordExpiryWarning,
        severity: "error",
      });
      setChangePass(true);
    }
  };

  // Only called by the idle-timeout fallback above, so this is always an auto-logout.
  const logoutAction = async (): Promise<void> => {
    try {
      await removeToken('auto');
    } catch (e: unknown) {
      console.error("Logout failed:", e);
    }
    sessionStorage.setItem("autoLogoutMessage", "1");
    localStorage.clear();
    window.location.pathname = "/login";
    window.location.pathname = window.location.pathname.split("?")[0];
  };

  return (
    <>
      {snack.open && <Snackbar {...snack} handleClose={() => setSnack(snackInitial)} />}
      <SimpleModal
        open={openChangePass}
        handleClose={() => setChangePass(!openChangePass)}
        body={() => <ChangePass setSnack={setSnack} setChangePass={setChangePass} forceChange />}
      />
      {children}
    </>
  );
};

export default AppLogout;
